import React from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Link from '@atlaskit/link/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import { messages } from '../../internal/credits-banner-content/messages';
import { getDepletedDescriptor } from '../../internal/credits-banner-content/scope';
import { CreditsSurface } from '../../internal/credits-surface';
import { RequestCreditsAction } from '../../internal/request-credits-action';
import { useCreditsUsageUrl } from '../../provider/credits-usage-context';
import type { CreditsBannerProps } from '../credits-banner';

export type CreditsInlineMessageProps = Omit<CreditsBannerProps, 'xcss'>;

const styles = cssMap({
	message: {
		color: token('color.text'),
		wordBreak: 'break-word',
	},
	warning: {
		color: token('color.text.subtlest'),
	},
	link: {
		color: token('color.text'),
		fontWeight: token('font.weight.medium'),
		textDecoration: 'underline',
	},
});

export const CreditsInlineMessage = ({
	additionalContext,
	showViewUsage = false,
	...props
}: CreditsInlineMessageProps): React.JSX.Element => {
	const usageUrl = useCreditsUsageUrl();
	const context = additionalContext?.trim();

	return (
		<CreditsSurface {...props}>
			{({ formattedResetDate, isWarning, canRequestCredits, scope, agentName }) => {
				const descriptor = isWarning
					? context
						? formattedResetDate
							? messages.warningWithContextAndResetDate
							: messages.warningWithContext
						: formattedResetDate
							? messages.warningWithResetDate
							: messages.warningWithoutResetDate
					: getDepletedDescriptor({
							scope,
							agentName,
							hasContext: Boolean(context),
							hasResetDate: Boolean(formattedResetDate),
						});

				return (
					<Inline alignBlock="center" space="space.100">
						<InformationCircleIcon
							label=""
							size="medium"
							color={isWarning ? token('color.icon.subtlest') : token('color.text')}
						/>
						<Box as="span" xcss={cx(styles.message, isWarning && styles.warning)}>
							<FormattedMessage
								{...descriptor}
								values={{
									additionalContext: context,
									resetDate: formattedResetDate,
									agentName,
								}}
							/>
							{canRequestCredits ? (
								<>
									{' '}
									<RequestCreditsAction>
										<Box as="span" xcss={cx(styles.link, isWarning && styles.warning)}>
											<FormattedMessage {...messages.requestMore} />
										</Box>
									</RequestCreditsAction>
								</>
							) : null}
							{showViewUsage && usageUrl ? (
								<>
									{' '}
									<FormattedMessage
										{...(canRequestCredits ? messages.orViewUsage : messages.viewUsage)}
										values={{
											link: (chunks) => (
												<Link
													appearance="subtle"
													href={usageUrl}
													target="_blank"
													rel="noopener noreferrer"
												>
													<Box as="span" xcss={cx(styles.link, isWarning && styles.warning)}>
														{chunks}
													</Box>
												</Link>
											),
										}}
									/>
								</>
							) : null}
						</Box>
					</Inline>
				);
			}}
		</CreditsSurface>
	);
};
